/**
 * GFG-364 QA round 2026-10-14 (R4): what the payment step shows when the funding wallet is short on
 * credits. Nothing is charged inside the additions flow; the client buys the missing credits in the
 * Wallet's Buy Credits modal. Pure: the quote, the caller's permission, the purchase made this
 * session, whether the wallet has a card and the campaign zone go in, a `CreditsShortfallView`
 * comes out.
 */

import { COPY } from "@/constants/campaignContacts";
import { CREDIT_INCREMENT } from "@/constants/review";
import type { RecipientAdditionsQuote } from "@/interfaces/campaignContacts";
import { PURCHASE_STATUS } from "@/interfaces/credits";
import { formatDateLong } from "@/utils/date-format";
import { zonedDateInput } from "./campaignContacts-helper";
import { wholeCredits } from "./cardPurchase-helper";
import type { AdditionCreditsCopy, CreditsShortfallView, WizardPurchase } from "./types";

interface ShortfallInput {
  quote: RecipientAdditionsQuote;
  /** `can("buy", "CREDIT")` for the signed-in user. */
  canBuyCredit: boolean;
  /** The purchase made in the Buy Credits modal this session. */
  purchase: WizardPurchase | null;
  /** The campaign's IANA zone, for the lot-expiry dates. */
  timeZone: string;
  /** Credits per dollar from the wallet's credit summary; null while unknown. */
  creditsPerUsd: number | null;
  /** Whether the funding wallet has a card the Buy Credits modal can charge. */
  hasCard: boolean;
}

/** Where a credit purchase stands, read from its status (`PURCHASE_STATUS`). */
export type PurchaseStatusOutcome = "success" | "failed" | "pending";

/** A purchase's status as the wizard reads it: settled, failed (or cancelled), else processing. */
export function purchaseStatusOutcome(status: number): PurchaseStatusOutcome {
  if (status === PURCHASE_STATUS.success) return "success";
  if (status === PURCHASE_STATUS.failed || status === PURCHASE_STATUS.cancelled) return "failed";
  return "pending";
}

/** How many dollar steps `buyCreditsUsd` tries before settling for the plain rounded-up amount. */
const MAX_USD_STEPS = 100;

/**
 * USD to buy at least `creditsToBuy` credits; 1 to 1 when no rate is configured. A purchase must be
 * a positive multiple of `CREDIT_INCREMENT` credits, so the shortfall is rounded up to one (as
 * `lib/checkout.ts` does), and the dollar amount is stepped up until the Buy Credits modal's own
 * conversion (`Math.round(usd × rate)`) lands on a multiple too.
 */
export function buyCreditsUsd(creditsToBuy: string, creditsPerUsd: number | null): number {
  const credits = Math.ceil(Number(creditsToBuy) / CREDIT_INCREMENT) * CREDIT_INCREMENT;
  if (creditsPerUsd === null || creditsPerUsd <= 0) return credits;
  const firstUsd = Math.ceil(credits / creditsPerUsd);
  const lands = (usd: number) => Math.round(usd * creditsPerUsd) % CREDIT_INCREMENT === 0;
  const step = Array.from({ length: MAX_USD_STEPS }, (_, i) => i).find((i) => lands(firstUsd + i));
  return firstUsd + (step ?? 0);
}

function lotExpiryView(
  block: NonNullable<RecipientAdditionsQuote["lotExpiry"]>,
  timeZone: string,
  override?: AdditionCreditsCopy,
): CreditsShortfallView {
  const copy = COPY.addWizard.credits;
  const lotExpiry = override?.lotExpiry ?? copy.lotExpiry;
  const lotExpiryCanAdd = override?.lotExpiryCanAdd ?? copy.lotExpiryCanAdd;
  const day = (iso: string) => formatDateLong(zonedDateInput(iso, timeZone) ?? "");
  return {
    kind: "lotExpiry",
    text: lotExpiry(day(block.purchaseExpiresAt), day(block.campaignCloseAt)),
    canAddText:
      block.affordableCount > 0
        ? lotExpiryCanAdd(block.affordableCount)
        : copy.lotExpiryNoneAffordable,
  };
}

/**
 * Precedence: nothing to buy; lot expiry (before any buy offer, buying cannot help); the postpaid
 * credit limit (buying cannot lift it, `creditLimitNotice` says why); a payment still processing;
 * BUY CREDITS for a caller who may buy in a group that may purchase, or the no-card message when
 * that wallet has no card to charge; else ask an admin.
 */
export function creditsShortfallView(
  i: ShortfallInput,
  creditsCopy?: AdditionCreditsCopy,
): CreditsShortfallView {
  const { quote } = i;
  const copy = COPY.addWizard.credits;
  if (quote.budget === null || quote.depositMode === "invoice") return { kind: "none" };
  if (Number(quote.creditsToBuy) <= 0) return { kind: "none" };
  if (quote.lotExpiry !== null) return lotExpiryView(quote.lotExpiry, i.timeZone, creditsCopy);
  if (quote.refusal === "credit_limit") return { kind: "none" };
  if (i.purchase?.outcome === "pending") {
    return { kind: "pending", text: copy.pending, checkAgain: copy.checkAgain };
  }
  if (i.canBuyCredit && quote.walletCanBuy) {
    if (!i.hasCard) return { kind: "noCard", text: copy.noCard };
    const usd = buyCreditsUsd(quote.creditsToBuy, i.creditsPerUsd);
    return { kind: "buy", initialUsd: usd, minUsd: usd, button: copy.buy };
  }
  const { credits, usd } = wholeCredits(quote.creditsToBuy);
  return { kind: "askAdmin", text: (creditsCopy?.askAdmin ?? copy.askAdmin)(credits, usd) };
}
